import sha1 from 'js-sha1'
import CommonsService from '@/services/commons.service'
import { Auth } from 'aws-amplify'
import { getTLDS, checkDomainAvailability, createSubscription, validateCoupon, checkSubDomainAvailability, createTrialSubscription, createBYODSubscription } from '@/core/http/signup.api'
import { saveItemInSessionStorage, getItemFromSessionStorage, removeItemFromSessionStorage, saveItemInLocalStorage, getItemFromLocalStorage } from '@/core/localstorage'
import AuthService from '../auth/Auth.service'
import Big from 'big.js'
import ToastService from '@/components/Toast/Toast.service'
import sessionService from '../session/session.service'

class SignUpService {
  sendUserCrendentials (fullname, email, password, invitationalCode = '') {
    const username = this.getUsernameFromEmail(email)
    return new Promise((resolve, reject) => {
      Auth.signUp({
        username,
        password,
        attributes: {
          email,
          name: fullname,
          'custom:invitational_code': invitationalCode,
        }
      }).then((response) => {
        const user = response?.user
        if (user?.username?.length > 0) {
          saveItemInSessionStorage('temporaryPass', password)
          saveItemInSessionStorage('temporaryMail', email)
          saveItemInLocalStorage('couponCode', invitationalCode)
          CommonsService.goToPage(`/auth/signup/verification-code?username=${username}`)
        }
        if (response.code === 'NotAuthorizedException') {
          ToastService.show({
            type: 'error',
            title: 'Error',
            message: response.message,
          })
        }
        resolve()
      })
        .catch((err) => {
          if (err.code) {
            if (err.message.toString().includes('Unrecognizable lambda output')) {
              ToastService.show({
                type: 'error',
                title: 'Error',
                message: 'Invalid invitational code',
              })
            } else {
              ToastService.show({
                type: 'error',
                title: 'Error',
                message: err.message,
              })
            }
          }
          reject()
        })
    })
  }

  getUsernameFromEmail (domain) {
    return sha1(domain)
  }

  async resendCode (username) {
    return new Promise((resolve, reject) => {
      Auth.resendSignUp(username).then(() => {
        ToastService.show({
          type: 'success',
          title: 'Success!',
          message: 'Verification code sent',
        })
        resolve()
      }).catch((err) => {
        if (err.code) {
          ToastService.show({
            type: 'error',
            title: 'Error',
            message: err.message,
          })
        }
        reject()
      })
    })
  }

  sendConfirmationCode (username, code) {
    return new Promise((resolve, reject) => {
      Auth.confirmSignUp(username, code).then((res) => {
        setTimeout(() => {
          const mail = getItemFromSessionStorage('temporaryMail')
          const pass = getItemFromSessionStorage('temporaryPass')
          if (mail && pass) {
            AuthService.signIn(mail, pass).then(() => {
              AuthService.setUserLogged()
              removeItemFromSessionStorage('temporaryMail')
              removeItemFromSessionStorage('temporaryPass')
              
              if (getItemFromLocalStorage('isfreetrial')) {
                CommonsService.goToPage('/auth/signup/choose-subdomain')
              } else {
                CommonsService.goToPage('/auth/signup/domain-selection')
              }
              resolve(res)
            }).catch((err) => {
              ToastService.show({
                type: 'error',
                title: 'Error',
                message: err.message,
              })
              reject()
            })
          } else {
            CommonsService.goToPage('/auth', true)
          }
        }, 500)
      }).catch((err) => {
        if (err.code) {
          ToastService.show({
            type: 'error',
            title: 'Error',
            message: err.message,
          })
        }
        reject()
      })
    })
  }

  orderDomain (domain) {
    CommonsService.goToPage(`/auth/signup/payment?domain=${domain}`)
  }

  orderDomainWithCoupon (domain, couponCode) {
    CommonsService.goToPage(`/auth/signup/payment?domain=${domain}&couponCode=${couponCode}`)
  }

  checkIfDomainIsAvailable (domain) {
    return new Promise((resolve) => {
      checkDomainAvailability(domain)
        .then((res) => {
          if (res.data.available) {
            resolve({
              available: true,
            })
          } else {
            resolve({ available: false, suggestions: res.data.suggestions })
          }
        })
        .catch(() => {
          resolve({ available: false })
        })
    })
  }

  async startFreeTrial (subdomain) {
    const res = await createTrialSubscription(subdomain)
    if (res.status !== 201) {
      return false
    }
    return true
  }

  async checkIfSubdomainIsAvailable (subdomain) {
    const res = await checkSubDomainAvailability(subdomain)
    if (res.status !== 200) {
      return { available: false }
    }
    return { available: true }
  }

  getTLDS () {
    return new Promise((resolve, reject) => {
      getTLDS().then((res) => {
        resolve(res.data)
      }).catch(() => reject())
    })
  }

  generateCustomer (isSandbox) {
    return new Promise((resolve, reject) => {
      createSubscription(
        CommonsService.getQueryParams('domain'),
        CommonsService.getQueryParams('couponCode'),
        isSandbox
      )
        .then((res) => {
          resolve(res.data)
        }).catch(() => {
          reject()
        })
    })
  }
  
  hasActiveSubscriptions (me) {
    if (Array.isArray(me.subscriptions) && me.subscriptions[0]) {
      return me.subscriptions.some((subscription) => subscription.status === 'active')
    }
    return false
  }

  returnToSignUpProcess (me) {
    if (!me.onboarding || !this.hasActiveSubscriptions(me)) {
      if (getItemFromLocalStorage('isfreetrial')) {
        return CommonsService.goToPage('/auth/signup/choose-subdomain')
      }
      if (sessionService.getUserStatus() === 'byod_pending') {
        return CommonsService.goToPage('/auth/signup/byod-setup-step')
      }
      return CommonsService.goToPage('/auth/signup/domain-selection')
    }
    if (me.status !== 'onboarding' && this.hasActiveSubscriptions(me)) {
      if (sessionService.getUserStatus() === 'byod_pending') {
        return CommonsService.goToPage('/auth/signup/byod-setup-step')
      } else {
        CommonsService.goToPage('/auth/signup/waiting-page')
      }
    }
  }

  getPricings (domainRegistrationPrice, domainRenewalPrice) {
    const MONTHLY_SUBSCRIPTION_FEE = Big(12.99)
    
    domainRenewalPrice = Big(domainRenewalPrice || domainRegistrationPrice)

    return {
      monthlySubscriptionFee: MONTHLY_SUBSCRIPTION_FEE.round(2),
      oneTimeCharge: Big(domainRegistrationPrice).add(MONTHLY_SUBSCRIPTION_FEE.add(domainRenewalPrice.div(12))).round(2),
      recurringCharge: MONTHLY_SUBSCRIPTION_FEE.add(domainRenewalPrice.div(12)).round(2)
    }
  }

  getDiscountAmount (pricing, discount) {
    if (discount.type === 'fixed') {
      return Big(Math.max(pricing.oneTimeCharge - discount.amount / 100, pricing.oneTimeCharge))
    } else {
      if (discount.scope === 'subscription_fee') {
        return this.getDiscountAmountForSubscriptionFee(pricing, discount)
      }
      return pricing.oneTimeCharge.times(Big(discount.percentage).div(100)).round(2)
    }
  }

  getDiscountAmountForSubscriptionFee (pricing, discount) {
    if (discount.type === 'percentage') {
      return pricing.monthlySubscriptionFee.times(Big(discount.percentage).div(100)).round(2)
    }
    return Big(0)
  }

  getTotalAmount (pricing, discount) {
    if (discount?.type) {
      return pricing.oneTimeCharge.sub(this.getDiscountAmount(pricing, discount).round(2)).round(2)
    }
    return pricing.oneTimeCharge
  }

  getRecurringAmount (pricing, discount) {
    if (discount?.type === 'percentage' && discount.scope === 'subscription_fee') {
      return pricing.recurringCharge.sub(this.getDiscountAmountForSubscriptionFee(pricing, discount).toNumber()).round(2)
    }
    return pricing.recurringCharge
  }

  async validateCoupon (couponCode) {
    const response = await validateCoupon(couponCode)
    const responseData = response.data
    if (responseData.percentage || responseData.amount || responseData.scope === 'ONLY_INVITATIONAL') {
      if (responseData.percentage) {
        return {
          type: 'percentage',
          scope: responseData.scope,
          percentage: responseData.percentage
        }
      } else if (responseData.amount) {
        return {
          type: 'fixed',
          amount: responseData.amount,
          scope: responseData.scope,
        }
      }
      return {
        scope: responseData.scope
      }
    } else {
      return false
    }
  }

  travelToNextPageInBYODMode () {
    CommonsService.goToPage('/auth/signup/byod-setup-step')
  }

  triggerCreateBYODSubscriptionRequest (couponCode) {
    return createBYODSubscription(couponCode)
  }
}

export default new SignUpService()
